import {
    Textfield,
    Selectfield,
    SelectOption,
    Fieldset,
    FieldsetLegend,
    Checkbox,
    Link,
    ValidationMessage,
    Button,
    useMediaQuery,
} from '@bi-digital/react';
import type { FormState, UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { WciaFormValues } from '../wciaSchema';
import styles from './WciaForm.module.css';
import { useProductPageContext } from '../../../../context/ProductPageContext';

interface WciaFormProps {
    formState: FormState<WciaFormValues>;
    register: UseFormRegister<WciaFormValues>;
    onSubmit: React.FormEventHandler<HTMLFormElement>;
}
const CONSENT_LINK = (language: string) =>
    language === 'en'
        ? 'https://www.bi.no/en/about-bi/privacy-policy-and-cookies/'
        : 'https://www.bi.no/om-bi/personvern-og-cookies/';

export const WciaForm = ({ formState, register, onSubmit }: WciaFormProps) => {
    const { t, i18n } = useTranslation();
    const { countryCodes } = useProductPageContext();
    const isLargeScreen = useMediaQuery('(min-width: 1128px)');
    const language = i18n.language as 'no' | 'en';

    return (
        <form className={styles.form} onSubmit={onSubmit} id='wciaForm'>
            <div className={styles.horizontalFields}>
                <Textfield
                    required
                    autoComplete='given-name'
                    label={t('cart:wcia.firstname')}
                    className={styles.field}
                    error={t(formState.errors.firstName?.message || '')}
                    {...register('firstName')}
                />
                <Textfield
                    required
                    autoComplete='family-name'
                    label={t('cart:wcia.lastname')}
                    className={styles.field}
                    error={t(formState.errors.lastName?.message || '')}
                    {...register('lastName')}
                />
            </div>
            <Textfield
                required
                className={styles.field}
                autoComplete='email'
                label={t('cart:wcia.email')}
                error={t(formState.errors.email?.message || '')}
                {...register('email')}
            />

            <div className={styles.horizontalFields}>
                <Selectfield
                    label={t('cart:wcia.phoneCountryCode')}
                    className={styles.field}
                    required
                    error={t(formState.errors.phoneCountryCode?.message || '')}
                    {...register('phoneCountryCode')}
                >
                    {countryCodes.map((item, idx) => (
                        <SelectOption label={item.displayValue[language]} key={idx} value={item.value} />
                    ))}
                </Selectfield>
                <Textfield
                    className={styles.field}
                    required
                    autoComplete='tel'
                    label={t('cart:wcia.phoneNumber')}
                    error={t(formState.errors.phoneNumber?.message || '')}
                    type='tel'
                    {...register('phoneNumber')}
                />
            </div>

            <Fieldset>
                <FieldsetLegend>{t('cart:wcia.dialogNewsletter')}</FieldsetLegend>
                <Checkbox
                    label={
                        <span className={styles.introText}>
                            {t('cart:wcia.dialogConsent')}{' '}
                            <Link href={CONSENT_LINK(language)} target='_blank'>
                                {t('cart:wcia.readMore')}
                            </Link>
                        </span>
                    }
                    error={Boolean(formState.errors.consent)}
                    fullwidth
                    {...register('consent')}
                />
                {formState.errors.consent?.message && (
                    <ValidationMessage>{t(formState.errors.consent.message)}</ValidationMessage>
                )}
            </Fieldset>
            {isLargeScreen && (
                // Hide submit button for smaller screens
                // Will appear in the parent Dialog component as a sticky button at the bottom of the screen
                <Button
                    fullwidth
                    size='large'
                    className={styles.submitButton}
                    submitting={formState.isSubmitting}
                    disabled={formState.isSubmitting}
                    type='submit'
                >
                    {t('cart:wcia.submit')}
                </Button>
            )}
        </form>
    );
};
